import { Icon, SegmentedCard } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { GIFT_MESSAGE_TYPE } from '@hero/constants';
import {
  DigitalWrapperSelection,
  RecipientExperiencePreview,
  RecipientGreetingCardPreview,
  ReviewSection
} from '@hero/molecules';

import * as S from './style';

export function RecipientExperienceSection({
  errors,
  giftWrapperUrl,
  linkPreview,
  logoUrl,
  digitalGreetingCard,
  defaultActiveTab = null,
  message,
  senderName,
  warnings,
  onMessageParams,
  onBranding,
  onUpdateAddons,
  isContactLess,
  stepIndex = 3,
  isCompleted = false
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(null);

  useEffect(() => {
    setTab(defaultActiveTab);
  }, [defaultActiveTab]);

  const experienceOptions = [
    {
      value: GIFT_MESSAGE_TYPE.inputMessage,
      title: t('standardGiftMessageExperienceTitle'),
      icon: <Icon name="standard-gift-message" size="xl" />,
      hiddenInput: false,
      description: t('standardGiftMessageExperienceDesc'),
      bottomContent: (
        <S.TabDescriptionText variant="caption1">
          <Icon name="success" />
          <Trans i18nKey="howToSendOption2DescriptionBox" components={{ b: <b /> }} />
        </S.TabDescriptionText>
      ),
      children: (
        <S.RECBody>
          <RecipientExperiencePreview
            errors={errors}
            giftWrapperUrl={giftWrapperUrl}
            isContactLess={isContactLess}
            linkPreview={linkPreview}
            logoUrl={logoUrl}
            message={message}
            senderName={senderName}
            onMessageParams={onMessageParams}
            onBranding={onBranding}
            warnings={warnings}
          />
          <DigitalWrapperSelection giftWrapperUrl={giftWrapperUrl} onBranding={onBranding} />
        </S.RECBody>
      )
    },
    {
      value: GIFT_MESSAGE_TYPE.cardMessage,
      title: t('greetingCardExperienceTitle'),
      description: t('greetingCardExperienceShortDesc'),
      icon: <Icon name="digital-greeting-card" size="xl" />,
      hiddenInput: false,
      bottomContent: (
        <S.TabDescriptionText variant="caption1">
          <Icon name="success" />
          <Trans i18nKey="howToSendOption2DescriptionBox" components={{ b: <b /> }} />
        </S.TabDescriptionText>
      ),
      children: (
        <S.RECBody>
          <RecipientGreetingCardPreview
            errors={errors}
            giftWrapperUrl={giftWrapperUrl}
            linkPreview={linkPreview}
            logoUrl={logoUrl}
            digitalGreetingCard={digitalGreetingCard}
            senderName={senderName}
            onBranding={onBranding}
            onMessageParams={onMessageParams}
            onUpdateAddons={onUpdateAddons}
          />
          <DigitalWrapperSelection giftWrapperUrl={giftWrapperUrl} onBranding={onBranding} />
        </S.RECBody>
      )
    }
  ];

  return (
    <ReviewSection
      id="section-recipient-experience"
      stepIndex={stepIndex}
      isCompleted={isCompleted}
      title={t('recipientExperienceTitle')}
      subTitle={t('recipientExperienceDesc')}
      warnings={warnings}
    >
      <S.RECHeader>
        <SegmentedCard
          activeTab={tab}
          onChange={setTab}
          updateHash={false}
          options={experienceOptions}
        />
      </S.RECHeader>
    </ReviewSection>
  );
}

RecipientExperienceSection.propTypes = {
  errors: PropTypes.shape({
    senderName: PropTypes.bool,
    message: PropTypes.bool
  }),
  giftWrapperUrl: PropTypes.string,
  isLoading: PropTypes.bool,
  linkPreview: PropTypes.string,
  logoUrl: PropTypes.string,
  message: PropTypes.string,
  digitalGreetingCard: PropTypes.object,
  onMessageParams: PropTypes.func.isRequired,
  onBranding: PropTypes.func.isRequired,
  senderName: PropTypes.string,
  isContactLess: PropTypes.bool,
  stepIndex: PropTypes.number,
  isCompleted: PropTypes.bool
};
